import React, { useState, useMemo } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  UserX,
  ChevronDown,
  ChevronUp,
  Info,
  CheckSquare,
  Square,
  Minus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
} from 'lucide-react';
import axios from 'axios';

interface ScoringRow {
  subscriberid: number;
  listid: number;
  emailaddress: string;
  subscribedate: number;
  sends_received: number;
  opens: number;
  bounce_count: number;
  score: number;
  score_reasons: string[];
}

interface Summary {
  total_scored: number;
  high: number;
  medium: number;
  low: number;
  segmentName?: string;
  segmentId?: number;
}

interface Segment {
  segmentid: number;
  segmentname: string;
}

interface List {
  listid: number;
  name?: string;
  listname?: string;
}

interface Props {
  segments: Segment[];
  lists: List[];
}

type SortKey =
  | 'emailaddress'
  | 'listid'
  | 'sends_received'
  | 'opens'
  | 'bounce_count'
  | 'score'
  | 'subscribedate';

const PAGE_SIZE = 50;

const SCORE_BADGE = (score: number) => {
  if (score >= 60) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (score >= 30) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
};

const SCORE_POINTS = [
  {
    points: 40,
    label: 'Multiple sends, zero opens',
    detail: 'Received at least the “Min sends” threshold and has never opened a campaign.',
  },
  {
    points: 30,
    label: 'Has bounced',
    detail: 'Appears in Interspire bounce history — high deliverability risk.',
  },
  {
    points: 15,
    label: 'Old + never opened',
    detail: 'Subscribed 3+ years ago and still has zero opens.',
  },
  {
    points: 10,
    label: 'Heavy send volume, no engagement',
    detail: 'Received 10+ campaigns with no opens (stacks with the +40 rule when both apply).',
  },
] as const;

const RISK_BANDS = [
  {
    band: 'High',
    range: '≥ 60',
    color: 'bg-red-500',
    text: 'text-red-700 dark:text-red-400',
    meaning:
      'Strong cleanup candidate — usually bounce history plus no opens, or several risk signals stacked.',
  },
  {
    band: 'Medium',
    range: '30–59',
    color: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
    meaning:
      'Clear disengagement — typically multiple sends with zero opens, and/or a bounce.',
  },
  {
    band: 'Low',
    range: '1–29',
    color: 'bg-yellow-400',
    text: 'text-yellow-700 dark:text-yellow-400',
    meaning:
      'Mild risk — often an old never-opener or light extra weight without a full medium pattern.',
  },
] as const;

function SortHeader({
  label,
  sortKey,
  activeKey,
  dir,
  align = 'left',
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right' | 'center';
  onSort: (key: SortKey) => void;
}) {
  const active = activeKey === sortKey;
  const alignCls =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <th className="px-3 py-2">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 w-full text-xs font-semibold uppercase tracking-wide hover:text-gray-800 dark:hover:text-slate-100 ${alignCls} ${
          active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-slate-400'
        }`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

export default function ListScoring({ segments, lists }: Props) {
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [segmentSearch, setSegmentSearch] = useState('');
  const [minSends, setMinSends] = useState(3);
  const [minScore, setMinScore] = useState(30);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [rows, setRows] = useState<ScoringRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scoredSegmentName, setScoredSegmentName] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [unsubbing, setUnsubbing] = useState(false);
  const [unsubResult, setUnsubResult] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [showScoreHelp, setShowScoreHelp] = useState(true);

  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const listName = (listid: number) =>
    lists.find((l) => l.listid === listid)?.name ??
    lists.find((l) => l.listid === listid)?.listname ??
    `#${listid}`;

  const filteredSegments = useMemo(() => {
    const q = segmentSearch.trim().toLowerCase();
    if (!q) return segments;
    return segments.filter(
      (s) =>
        s.segmentname.toLowerCase().includes(q) ||
        String(s.segmentid).includes(q)
    );
  }, [segments, segmentSearch]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'emailaddress':
          cmp = a.emailaddress.localeCompare(b.emailaddress);
          break;
        case 'listid':
          cmp = listName(a.listid).localeCompare(listName(b.listid));
          break;
        case 'sends_received':
          cmp = a.sends_received - b.sends_received;
          break;
        case 'opens':
          cmp = a.opens - b.opens;
          break;
        case 'bounce_count':
          cmp = a.bounce_count - b.bounce_count;
          break;
        case 'score':
          cmp = a.score - b.score;
          break;
        case 'subscribedate':
          cmp = a.subscribedate - b.subscribedate;
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) cmp = a.emailaddress.localeCompare(b.emailaddress);
      return cmp * dir;
    });
    return copy;
    // listName uses lists; include lists so rename/load updates sort
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir, lists]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageRows = sortedRows.slice(pageStart, pageStart + PAGE_SIZE);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Score/sends/bounces default high→low; text/date default A→Z / oldest→newest
      setSortDir(
        key === 'emailaddress' || key === 'listid' || key === 'subscribedate' ? 'asc' : 'desc'
      );
    }
    setPage(1);
  };

  const loadRows = async (sid: number) => {
    setLoading(true);
    setSummaryLoading(true);
    setError(null);
    setRows([]);
    setSelected(new Set());
    setUnsubResult(null);
    setSummary(null);
    setPage(1);
    setExpandedRow(null);
    try {
      const params = new URLSearchParams({
        segmentId: String(sid),
        minSends: String(minSends),
        minScore: String(minScore),
        limit: '10000',
        cursor: '',
      });
      const res = await axios.get<{
        rows: ScoringRow[];
        has_more: boolean;
        segmentName?: string;
        summary?: Summary;
      }>(`/api/list-scoring?${params}`, { timeout: 120_000 });
      setRows(res.data.rows);
      if (res.data.segmentName) setScoredSegmentName(res.data.segmentName);
      if (res.data.summary) {
        setSummary({
          ...res.data.summary,
          segmentName: res.data.segmentName,
          segmentId: sid,
        });
      }
      if (res.data.has_more) {
        setError(
          `Showing the first ${res.data.rows.length.toLocaleString()} matches (API cap). Narrow filters if you need a smaller set.`
        );
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const msg =
        status === 504
          ? 'Scoring timed out (504). Try again — large segments can take a minute on first run.'
          : (e?.response?.data?.error ?? e?.message ?? 'Failed to load scoring results');
      setError(msg);
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  };

  const handleRun = () => {
    if (segmentId === '') {
      setError('Select a segment before running scoring.');
      return;
    }
    loadRows(segmentId);
  };

  const toggleRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectPage = () => setSelected(new Set(pageRows.map((r) => r.subscriberid)));
  const selectAllResults = () => setSelected(new Set(sortedRows.map((r) => r.subscriberid)));
  const selectNone = () => setSelected(new Set());

  const handleBulkUnsub = async () => {
    if (!selected.size) return;
    const selectedRows = rows.filter((r) => selected.has(r.subscriberid));

    const byList = new Map<number, number[]>();
    for (const r of selectedRows) {
      if (!byList.has(r.listid)) byList.set(r.listid, []);
      byList.get(r.listid)!.push(r.subscriberid);
    }

    if (
      !window.confirm(
        `Unsubscribe ${selected.size.toLocaleString()} contact${selected.size !== 1 ? 's' : ''}? This cannot be undone.`
      )
    ) {
      return;
    }

    setUnsubbing(true);
    setUnsubResult(null);
    let total = 0;
    try {
      for (const lid of Array.from(byList.keys())) {
        const ids = byList.get(lid)!;
        for (let i = 0; i < ids.length; i += 1000) {
          const batch = ids.slice(i, i + 1000);
          const res = await axios.post<{ unsubscribed: number }>('/api/list-scoring/bulk-unsubscribe', {
            subscriberIds: batch,
            listId: lid,
          });
          total += res.data.unsubscribed;
        }
      }

      setUnsubResult(`${total.toLocaleString()} contact${total !== 1 ? 's' : ''} unsubscribed.`);
      setRows((prev) => prev.filter((r) => !selected.has(r.subscriberid)));
      setSelected(new Set());
      setPage(1);
    } catch (e: any) {
      setUnsubResult('Error: ' + (e?.response?.data?.error ?? e?.message ?? 'Unsubscribe failed'));
    } finally {
      setUnsubbing(false);
    }
  };

  const pageAllSelected =
    pageRows.length > 0 && pageRows.every((r) => selected.has(r.subscriberid));
  const pageSomeSelected = pageRows.some((r) => selected.has(r.subscriberid)) && !pageAllSelected;
  const canRun = segmentId !== '' && !loading && !summaryLoading;
  const showingFrom = sortedRows.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PAGE_SIZE, sortedRows.length);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Segment Scoring
        </h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
          Flags contacts inside a selected segment who look like spamtraps, dead addresses, or
          long-term non-openers. A segment is required so we never scan the full Master List.
        </p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[260px] flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Segment <span className="text-red-500">*</span>
            </label>
            <input
              type="search"
              value={segmentSearch}
              onChange={(e) => setSegmentSearch(e.target.value)}
              placeholder="Filter segments…"
              className="mb-1.5 w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5"
            />
            <select
              value={segmentId}
              onChange={(e) => setSegmentId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5"
            >
              <option value="">Select a segment…</option>
              {filteredSegments.map((s) => (
                <option key={s.segmentid} value={s.segmentid}>
                  {s.segmentname} (ID {s.segmentid})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Min sends received
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={minSends}
              onChange={(e) => setMinSends(Math.max(1, Math.min(50, Number(e.target.value) || 3)))}
              className="w-20 text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-center"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Min risk score
            </label>
            <select
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              className="text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5"
            >
              <option value={60}>High only (≥60)</option>
              <option value={30}>Medium + High (≥30)</option>
              <option value={1}>All risk levels (≥1)</option>
            </select>
          </div>

          <button
            onClick={handleRun}
            disabled={!canRun}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading || summaryLoading ? 'animate-spin' : ''}`} />
            Run Scoring
          </button>
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-slate-800/80 rounded-lg border border-gray-200 dark:border-slate-700">
        <button
          type="button"
          onClick={() => setShowScoreHelp((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
        >
          <span className="text-sm font-semibold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-500" />
            How scores &amp; risk levels work
          </span>
          {showScoreHelp ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </button>
        {showScoreHelp && (
          <div className="px-4 pb-4 space-y-4 border-t border-gray-200 dark:border-slate-700 pt-3">
            <p className="text-sm text-gray-600 dark:text-slate-300">
              Each contact starts at <strong>0</strong>. Matching rules add points. Only contacts with a
              score above 0 (and at least “Min sends”) appear. Sends are counted from campaigns in
              roughly the last two years.
            </p>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-2">
                Point rules
              </h3>
              <ul className="space-y-2">
                {SCORE_POINTS.map((r) => (
                  <li key={r.points} className="flex gap-3 text-sm">
                    <span className="shrink-0 font-mono font-bold text-gray-900 dark:text-white w-10">
                      +{r.points}
                    </span>
                    <span>
                      <span className="font-medium text-gray-800 dark:text-slate-100">{r.label}</span>
                      <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                        {r.detail}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-2">
                Risk bands
              </h3>
              <ul className="space-y-2">
                {RISK_BANDS.map((b) => (
                  <li key={b.band} className="flex gap-3 text-sm">
                    <span className="shrink-0 inline-flex items-center gap-1.5 w-28">
                      <span className={`inline-block w-2 h-2 rounded-full ${b.color}`} />
                      <span className={`font-semibold ${b.text}`}>{b.band}</span>
                    </span>
                    <span>
                      <span className="font-mono text-xs text-gray-500 dark:text-slate-400 mr-2">
                        {b.range}
                      </span>
                      <span className="text-gray-600 dark:text-slate-300">{b.meaning}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-gray-500 dark:text-slate-400">
              Example: 5 sends / 0 opens (+40) and a bounce (+30) → score <strong>70</strong> (High).
              Expand a row’s chevron to see which rules fired for that contact.
            </p>
          </div>
        )}
      </div>

      {scoredSegmentName && (summary || rows.length > 0) && (
        <p className="text-sm text-gray-600 dark:text-slate-300">
          Results for <span className="font-semibold text-gray-900 dark:text-white">{scoredSegmentName}</span>
        </p>
      )}

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total flagged', value: summary.total_scored, color: 'text-gray-700 dark:text-white' },
            { label: 'High risk', value: summary.high, color: 'text-red-600 dark:text-red-400' },
            { label: 'Medium risk', value: summary.medium, color: 'text-amber-600 dark:text-amber-400' },
            { label: 'Low risk', value: summary.low, color: 'text-yellow-600 dark:text-yellow-400' },
          ].map((c) => (
            <div
              key={c.label}
              className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-3"
            >
              <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">{c.label}</div>
              <div className={`text-2xl font-bold ${c.color}`}>{c.value.toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {unsubResult && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm border ${
            unsubResult.startsWith('Error')
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-400'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-700 dark:text-green-400'
          }`}
        >
          {unsubResult}
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex-wrap">
            <button
              onClick={pageAllSelected ? selectNone : selectPage}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-slate-300 hover:text-gray-900"
            >
              {pageAllSelected ? (
                <CheckSquare className="h-4 w-4" />
              ) : pageSomeSelected ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              {pageAllSelected ? 'Deselect page' : `Select page (${pageRows.length})`}
            </button>
            {sortedRows.length > pageRows.length && (
              <button
                type="button"
                onClick={selectAllResults}
                className="text-sm text-blue-600 hover:underline"
              >
                Select all {sortedRows.length.toLocaleString()}
              </button>
            )}
            <span className="text-sm text-gray-500 dark:text-slate-400">
              {selected.size.toLocaleString()} selected
            </span>
            <button
              onClick={handleBulkUnsub}
              disabled={selected.size === 0 || unsubbing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 ml-auto"
            >
              <UserX className="h-4 w-4" />
              {unsubbing
                ? 'Unsubscribing…'
                : `Unsubscribe selected (${selected.size.toLocaleString()})`}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-700/60">
                <tr>
                  <th className="w-10 px-3 py-2"></th>
                  <SortHeader
                    label="Email"
                    sortKey="emailaddress"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="List"
                    sortKey="listid"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Sends"
                    sortKey="sends_received"
                    activeKey={sortKey}
                    dir={sortDir}
                    align="right"
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Opens"
                    sortKey="opens"
                    activeKey={sortKey}
                    dir={sortDir}
                    align="right"
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Bounces"
                    sortKey="bounce_count"
                    activeKey={sortKey}
                    dir={sortDir}
                    align="right"
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Score"
                    sortKey="score"
                    activeKey={sortKey}
                    dir={sortDir}
                    align="center"
                    onSort={handleSort}
                  />
                  <SortHeader
                    label="Subscribed"
                    sortKey="subscribedate"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {pageRows.map((row) => (
                  <React.Fragment key={row.subscriberid}>
                    <tr
                      className={`hover:bg-gray-50 dark:hover:bg-slate-700/40 ${
                        selected.has(row.subscriberid) ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(row.subscriberid)}
                          onChange={() => toggleRow(row.subscriberid)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                      </td>
                      <td
                        className="px-3 py-2 font-medium text-gray-900 dark:text-slate-100 max-w-[220px] truncate"
                        title={row.emailaddress}
                      >
                        {row.emailaddress}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-slate-400 text-xs">
                        {listName(row.listid)}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600 dark:text-slate-300">
                        {row.sends_received}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600 dark:text-slate-300">
                        {row.opens}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={
                            row.bounce_count > 0
                              ? 'text-red-600 dark:text-red-400 font-medium'
                              : 'text-gray-600 dark:text-slate-300'
                          }
                        >
                          {row.bounce_count}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${SCORE_BADGE(row.score)}`}
                        >
                          {row.score}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap">
                        {row.subscribedate > 0
                          ? new Date(row.subscribedate * 1000).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() =>
                            setExpandedRow(expandedRow === row.subscriberid ? null : row.subscriberid)
                          }
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                          title="Show reasons"
                        >
                          {expandedRow === row.subscriberid ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedRow === row.subscriberid && (
                      <tr className="bg-gray-50 dark:bg-slate-700/20">
                        <td colSpan={9} className="px-6 py-2 text-xs text-gray-600 dark:text-slate-400">
                          <Info className="h-3.5 w-3.5 inline mr-1.5 text-blue-500" />
                          {row.score_reasons.join(' · ')}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of{' '}
              {sortedRows.length.toLocaleString()}
              {PAGE_SIZE > 0 ? ` · ${PAGE_SIZE} per page` : ''}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-gray-300 dark:border-slate-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <span className="text-sm text-gray-600 dark:text-slate-300 tabular-nums px-1">
                Page {safePage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-gray-300 dark:border-slate-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && rows.length === 0 && !error && summary !== null && (
        <div className="text-center py-12 text-gray-400 dark:text-slate-500">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No contacts in this segment match the current scoring criteria.</p>
          <p className="text-xs mt-1">Try lowering the minimum sends or score threshold.</p>
        </div>
      )}

      {!loading && rows.length === 0 && !error && summary === null && segmentId === '' && (
        <div className="text-center py-12 text-gray-400 dark:text-slate-500">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a segment, then click Run Scoring.</p>
        </div>
      )}
    </div>
  );
}
